import type { ActiveCapabilities, CommandDef, ParsedCommand } from './types';

/**
 * Maximum number of suggestion rows the palette will surface for a
 * non-empty parsed name. Chosen to match the keyboard cursor + visual
 * density of the existing recents dropdown (~8 rows fit without
 * scrolling on a default-height search bar).
 */
const MAX_SUGGESTIONS = 8;

/**
 * Capability gate. Mirrors `useHasCapability`'s ALL-of semantics:
 *   - no `capability` declared → always available
 *   - string `capability`      → present in `active`
 *   - array `capability`       → every key present in `active`
 */
export function isCommandAuthorized(
  def: CommandDef,
  active: ActiveCapabilities,
): boolean {
  if (!def.capability) return true;
  if (typeof def.capability === 'string') {
    return Boolean(active[def.capability]);
  }
  return def.capability.every(key => Boolean(active[key]));
}

/**
 * Filter a registry slice by the active capability set. Pure helper
 * around `isCommandAuthorized`; broken out so callers (matcher, host
 * for an exact-id execute) can share the same gate.
 */
export function filterAuthorizedCommands(
  defs: readonly CommandDef[],
  active: ActiveCapabilities,
): CommandDef[] {
  return defs.filter(def => isCommandAuthorized(def, active));
}

/**
 * Score a single command def against a parsed name. Higher is better.
 * Returns `null` for non-matches so the matcher can drop them.
 *
 * Scoring tiers:
 *   3 — id is an exact prefix of the typed name OR vice versa
 *   2 — any alias is a prefix match (either direction)
 *   1 — id contains the typed name as a substring (after position 0)
 *   0 — alias contains the typed name as a substring
 */
function scoreCommand(def: CommandDef, name: string): number | null {
  if (!name) return 0;
  const id = def.id;

  if (id.startsWith(name) || name.startsWith(id)) return 3;
  for (const alias of def.aliases ?? []) {
    if (alias.startsWith(name) || name.startsWith(alias)) return 2;
  }
  if (id.includes(name)) return 1;
  for (const alias of def.aliases ?? []) {
    if (alias.includes(name)) return 0;
  }
  return null;
}

/**
 * Resolve a parsed input to ranked command suggestions, after the
 * capability filter. The empty-name case (just `/` typed) returns the
 * full authorized catalog in registration order — this is what the
 * palette renders "in place of recent searches" on the slash press.
 *
 * Stable sort: ties break by the command's original position in
 * `defs`, which mirrors the registration order.
 */
export function matchCommands({
  parsed,
  defs,
  capabilities,
}: {
  parsed: ParsedCommand;
  defs: readonly CommandDef[];
  capabilities: ActiveCapabilities;
}): CommandDef[] {
  const authorized = filterAuthorizedCommands(defs, capabilities);

  if (parsed.name === '') return authorized;

  const scored: { def: CommandDef; score: number; idx: number }[] = [];
  authorized.forEach((def, idx) => {
    const score = scoreCommand(def, parsed.name);
    if (score === null) return;
    scored.push({ def, score, idx });
  });

  scored.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.idx - b.idx;
  });

  return scored.slice(0, MAX_SUGGESTIONS).map(s => s.def);
}

/**
 * Resolve a parsed input to a *committed* command — i.e. one whose id
 * or alias exactly equals the parsed name. This is what the host uses
 * to decide between palette suggestion-mode and arg/scoped-search
 * bridge mode. Returns `undefined` for "not committed yet".
 *
 * The capability filter is applied here too: an unauthorized command
 * is treated as if it didn't exist, so the user gets the regular
 * "no matches" surface rather than an inert command that does
 * nothing on Enter.
 */
export function findCommittedCommand({
  parsed,
  defs,
  capabilities,
}: {
  parsed: ParsedCommand;
  defs: readonly CommandDef[];
  capabilities: ActiveCapabilities;
}): CommandDef | undefined {
  if (parsed.name === '') return undefined;

  const authorized = filterAuthorizedCommands(defs, capabilities);
  return authorized.find(
    def => def.id === parsed.name || (def.aliases ?? []).includes(parsed.name),
  );
}
